import { Button, Group, Stack, Tooltip } from '@mantine/core';
import { useField } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { Transit } from '@e-origin/shared';

import { TextInput } from '@components';
import { useAmendTransit, useDuplicateTransit } from '@hooks/transit';
import { canTransitBeAmended } from '../helpers';

interface DuplicateTransitProps {
  transit: Transit;
  onHide(): void;
}

const nameSchema = z
  .string()
  .min(1, { message: 'Required' })
  .regex(/^[a-zA-Z0-9-]*$/g, { message: 'Name is invalid' });

export const DuplicateTransit: React.FC<DuplicateTransitProps> = ({ transit, onHide }) => {
  const duplicateTransitMutation = useDuplicateTransit();
  const amendTransitMutation = useAmendTransit();
  const canAmend = canTransitBeAmended(transit);

  const nameField = useField({
    initialValue: '',
    validate: (value) => (nameSchema.safeParse(value).success ? null : 'Name is invalid'),
    validateOnBlur: true,
  });

  useEffect(() => {
    if (transit) {
      const prefix = canAmend ? 'AMEND' : 'DUPL';
      nameField.setValue(`${prefix}-${transit.name}`);
    }
  }, [transit]);

  const handleAmend = async () => {
    await amendTransitMutation.mutateAsync({
      transitId: transit._id,
      name: nameField.getValue(),
    });

    onHide();
  };

  const handleDuplicate = async () => {
    await duplicateTransitMutation.mutateAsync({
      transitId: transit._id,
      name: nameField.getValue(),
    });

    onHide();
  };

  const isPending = duplicateTransitMutation.isPending || amendTransitMutation.isPending;
  const isValid = !nameField.error;

  return (
    <Stack>
      <TextInput {...nameField.getInputProps()} label="Transit name" size="lg" data-pw="duplicate-transit-name-input" w="100%" />

      <Group justify="center" mt="xl">
        <Button type="button" onClick={onHide} disabled={isPending} size="lg" variant="transparent">
          Cancel
        </Button>

        {canAmend && (
          <Tooltip label="Amend">
            <Button type="button" onClick={handleAmend} loading={amendTransitMutation.isPending} disabled={!isValid} variant="filled" size="lg">
              Amendment
            </Button>
          </Tooltip>
        )}

        <Tooltip label="Duplicate">
          <Button type="button" onClick={handleDuplicate} loading={duplicateTransitMutation.isPending} disabled={!isValid} variant="filled" size="lg">
            Duplicate
          </Button>
        </Tooltip>
      </Group>
    </Stack>
  );
};
